import React, { useEffect, useMemo, useState } from "react";

import { IDDateRange, MonthlyYearGroup, Option } from "../../types";
import { IDDateRangeOption } from "../../InsightsDashboard";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { CustomDateRange } from "src/app/components/CustomDateRange/CustomDateRange";
import { PillSelector } from "src/app/components/PillSelector/PillSelector";
import classNames from "classnames";

import "./IDDateSelector.scss";

interface Props {
  items: IDDateRange[];
  value: IDDateRangeOption | null;
  onChange: (value: IDDateRangeOption) => void;
  dateFrom: string | null;
  setDateFrom: (newDate: string | null) => void;
  dateTo: string | null;
  setDateTo: (newDate: string | null) => void;
  onCustomRangeChange?: (dateFrom: string, dateTo: string) => void;
  customDateError?: boolean;
  customDateErrorMessage?: React.ReactNode;
}

const findInitialSection = (
  items: IDDateRange[],
  value: IDDateRangeOption | null,
) => {
  if (value) {
    for (const section of items) {
      if (section.type === "monthly") {
        if (
          section.years.some((y) =>
            y.options.some((o) => o.value === value.value),
          )
        ) {
          return section;
        }
      } else if (section.type === "custom") {
        if (value.value === "custom") return section;
      } else if (section.options.some((o) => o.value === value.value)) {
        return section;
      }
    }
  }

  return items.find((item) => item.type === "quick") ?? items[0] ?? null;
};

const findInitialYear = (
  section: IDDateRange | null,
  value: IDDateRangeOption | null,
) => {
  if (!section || section.type !== "monthly" || !value) return null;

  return (
    section.years.find((y) => y.options.some((o) => o.value === value.value)) ??
    null
  );
};

export const IDDateSelector = ({
  items,
  value,
  onChange,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  onCustomRangeChange,
  customDateError,
  customDateErrorMessage,
}: Props) => {
  const [activeSection, setActiveSection] = useState<IDDateRange | null>(() =>
    findInitialSection(items, value),
  );
  const [activeYear, setActiveYear] = useState<MonthlyYearGroup | null>(() =>
    findInitialYear(findInitialSection(items, value), value),
  );

  useEffect(() => {
    // `items` (dateRanges config) loads asynchronously, so the very first
    // render can mount this with an empty list — re-sync once it arrives,
    // but only if the user hasn't picked a section yet.
    if (activeSection !== null || !items.length) return;

    const initialSection = findInitialSection(items, value);
    setActiveSection(initialSection);
    setActiveYear(findInitialYear(initialSection, value));
  }, [items, value, activeSection]);

  const secondLevel = useMemo<MonthlyYearGroup[] | Option[]>(() => {
    if (!activeSection) return [];

    if (activeSection.type === "monthly") {
      return activeSection.years;
    }

    if (activeSection.type === "custom") {
      return [];
    }

    return activeSection.options;
  }, [activeSection]);

  const showThird = activeSection?.type === "monthly" && activeYear;

  const handleSectionClick = (sectionType: string | null) => {
    const section = items.find((item) => item.type === sectionType) ?? null;
    let nextActiveYear: MonthlyYearGroup | null = null;

    if (sectionType === "custom") {
      if (activeSection?.type !== "custom") {
        // Entering custom should always start from an empty state,
        // otherwise dates left over from a previous custom pick (or the
        // default range) show up pre-filled as if already chosen/applied.
        setDateFrom(null);
        setDateTo(null);
        // Reflect the section switch in the committed value immediately,
        // so Apply validates against "custom with no dates picked yet"
        // instead of silently resubmitting whatever was selected before.
        onChange({ value: "custom", label: "Custom" });
      }
    } else if (activeSection?.type !== sectionType) {
      const sectionOptions: Option[] =
        section?.type === "monthly"
          ? section.years.flatMap((year) => year.options)
          : section?.options ?? [];
      const hasMatchingOption = sectionOptions.some(
        (opt) => opt.value === value?.value,
      );

      if (hasMatchingOption) {
        nextActiveYear = findInitialYear(section, value);
      } else if (sectionOptions.length > 0) {
        // Nothing in the newly opened section matches the currently
        // selected option (e.g. coming from Custom, or the value belongs to
        // another section) — default to its last (most recent) chip, so
        // Apply always commits something valid instead of silently
        // resubmitting whatever was selected before. Consistent across
        // every preset section: Quick, Yearly, Quarterly, Monthly.
        const lastOption = sectionOptions[sectionOptions.length - 1];
        onChange(lastOption);

        if (section?.type === "monthly") {
          nextActiveYear =
            section.years.find((year) =>
              year.options.some((opt) => opt.value === lastOption.value),
            ) ?? null;
        }
      }
    }

    setActiveSection(section);
    setActiveYear(nextActiveYear);
  };

  const handleYearClick = (year: MonthlyYearGroup) => {
    setActiveYear(year);
  };

  const handleOptionClick = (optionValue: IDDateRangeOption) => {
    onChange(optionValue);
  };

  const handleCustomRangeComplete = (start: string, end: string) => {
    if (onCustomRangeChange) {
      onCustomRangeChange(start, end);
    } else {
      setDateFrom(start);
      setDateTo(end);
      onChange({ value: "custom", label: "Custom" });
    }
  };

  return (
    <>
      <span className="id-date-selector__label">
        <IDHFormattedMessage id="ws_time_range" defaultMessage="Time range" />
      </span>
      <div className="id-date-selector">
        <PillSelector
          className="id-date-selector__tabs"
          options={items.map((section) => ({
            value: section.type,
            label: section.label,
          }))}
          value={activeSection?.type}
          onChange={handleSectionClick}
        />

        {activeSection && activeSection.type !== "custom" && (
          <div className="id-date-selector__chips-row">
            {activeSection.type === "monthly"
              ? (secondLevel as MonthlyYearGroup[]).map((year) => (
                  <button
                    type="button"
                    key={year.year}
                    className={classNames("id-date-selector__chip", {
                      "id-date-selector__chip--selected":
                        activeYear?.year === year.year,
                    })}
                    onClick={() => handleYearClick(year)}
                  >
                    {year.year}
                  </button>
                ))
              : (secondLevel as Option[]).map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    className={classNames("id-date-selector__chip", {
                      "id-date-selector__chip--selected":
                        value?.value === opt.value,
                    })}
                    onClick={() => handleOptionClick(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
          </div>
        )}

        {showThird && (
          <div className="id-date-selector__chips-row">
            {activeYear!.options.map((opt) => (
              <button
                type="button"
                key={opt.value}
                className={classNames("id-date-selector__chip", {
                  "id-date-selector__chip--selected":
                    value?.value === opt.value,
                })}
                onClick={() => handleOptionClick(opt)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {activeSection?.type === "custom" && (
          <div className="id-date-selector__custom">
            <CustomDateRange
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              onRangeComplete={handleCustomRangeComplete}
              minDate={new Date("2024-01-01")}
              hideClearButton
              showError={customDateError}
              errorMessage={customDateErrorMessage}
            />
          </div>
        )}
      </div>
    </>
  );
};
